import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertyApi, type PropertyData } from '../services';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import './PropertiesPage.css';
import DonutChart from '../components/DonutChart';
import PolygonUp from '../components/PolygonUp';
import PolygonDown from '../components/PolygonDown';

const PropertiesPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const { user } = useAuth();
  const [properties, setProperties] = useState<PropertyData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [chartData, setChartData] = useState<
    { label: string; value: number }[]
  >([]);
  const [loadingChart, setLoadingChart] = useState<boolean>(false);
  const [isMapInitialized, setIsMapInitialized] = useState<boolean>(false);

  // Refs for map
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);

  // Helper function to normalize property data
  const normalizeProperty = (prop: PropertyData): PropertyData => {
    return {
      ...prop,
      kaufpreis:
        prop.kaufpreis !== undefined && prop.kaufpreis !== null
          ? typeof prop.kaufpreis === 'string'
            ? parseFloat(prop.kaufpreis)
            : prop.kaufpreis
          : undefined,
      currentValue:
        prop.currentValue !== undefined && prop.currentValue !== null
          ? typeof prop.currentValue === 'string'
            ? parseFloat(prop.currentValue)
            : prop.currentValue
          : undefined,
      gain: typeof prop.gain === 'string' ? parseFloat(prop.gain) : prop.gain,
      gainPercentage:
        typeof prop.gainPercentage === 'string'
          ? parseFloat(prop.gainPercentage)
          : prop.gainPercentage,
      latitude:
        prop.latitude !== undefined && prop.latitude !== null
          ? typeof prop.latitude === 'string'
            ? parseFloat(prop.latitude)
            : prop.latitude
          : undefined,
      longitude:
        prop.longitude !== undefined && prop.longitude !== null
          ? typeof prop.longitude === 'string'
            ? parseFloat(prop.longitude)
            : prop.longitude
          : undefined,
      sellDate: prop.sellDate
        ? typeof prop.sellDate === 'string'
          ? prop.sellDate.split('T')[0]
          : new Date(prop.sellDate).toISOString().split('T')[0]
        : undefined,
      sellAmount:
        prop.sellAmount !== undefined && prop.sellAmount !== null
          ? typeof prop.sellAmount === 'string'
            ? parseFloat(prop.sellAmount)
            : prop.sellAmount
          : undefined,
    };
  };

  const fetchChartData = async () => {
    try {
      setLoadingChart(true);
      const data = await propertyApi.getCityDistribution();
      setChartData(data);
    } catch (error: any) {
      console.error('Failed to load chart data:', error);
    } finally {
      setLoadingChart(false);
    }
  };

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        const data = await propertyApi.getAll();
        const normalizedData = data.map(normalizeProperty);
        setProperties(normalizedData);
      } catch (error: any) {
        showError(error.message || 'Failed to load properties');
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [properties.length]); // Refresh when property count changes

  // Function to initialize map
  const initializeMap = () => {
    if (!mapRef.current) return false;

    const googleMaps = (window as any).google;
    if (!googleMaps || !googleMaps.maps || !googleMaps.maps.Map) return false;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new googleMaps.maps.Map(mapRef.current, {
        center: { lat: 47.5162, lng: 14.5501 }, // Default to Austria
        zoom: 7,
        disableDefaultUI: true,
      });
      setIsMapInitialized(true);
    }
    return true;
  };

  // Initialize map on mount/load
  useEffect(() => {
    let isMounted = true;
    const initTimeout = setTimeout(() => {
      if (!isMounted) return;

      if (!initializeMap()) {
        let retryCount = 0;
        const maxRetries = 100;
        const retryInterval = setInterval(() => {
          if (!isMounted) {
            clearInterval(retryInterval);
            return;
          }
          retryCount++;
          if (initializeMap() || retryCount >= maxRetries) {
            clearInterval(retryInterval);
          }
        }, 100);
      }
    }, 50);

    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
    };
  }, []);

  // Update markers and bounds when properties change
  useEffect(() => {
    if (!isMapInitialized || !mapInstanceRef.current) return;

    const googleMaps = (window as any).google;
    if (!googleMaps || !googleMaps.maps) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    const bounds = new googleMaps.maps.LatLngBounds();
    let hasMarkers = false;

    properties.forEach((property) => {
      if (property.latitude && property.longitude) {
        const position = { lat: property.latitude, lng: property.longitude };
        const marker = new googleMaps.maps.Marker({
          position,
          map: mapInstanceRef.current,
          title: property.address,
        });

        // Add click listener to navigate to property page
        marker.addListener('click', () => {
          navigate(`/properties/${property.id}`);
        });

        markersRef.current.push(marker);
        bounds.extend(position);
        hasMarkers = true;
      }
    });

    if (hasMarkers) {
      mapInstanceRef.current.fitBounds(bounds);
      // Don't zoom in too much for a single marker
      const listener = googleMaps.maps.event.addListener(
        mapInstanceRef.current,
        'idle',
        () => {
          if (
            mapInstanceRef.current &&
            mapInstanceRef.current.getZoom()! > 15
          ) {
            mapInstanceRef.current.setZoom(15);
          }
          googleMaps.maps.event.removeListener(listener);
        },
      );
    }
  }, [properties, isMapInitialized, navigate]);

  const handlePropertyClick = (id: string) => {
    navigate(`/properties/${id}`);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getPropertyRank = (prop: PropertyData): number => {
    if (prop.sellDate) {
      return 1; // verkauft
    }
    const hasKaufpreis =
      prop.kaufpreis !== undefined && prop.kaufpreis !== null;
    const hasKaufdatum = !!prop.kaufdatum;
    if (hasKaufpreis && hasKaufdatum) {
      return 0; // in besitz
    }
    return 2; // watchlist
  };

  const propertiesInBesitz = properties.filter((p) => getPropertyRank(p) === 0);
  const propertiesVerkauft = properties.filter((p) => getPropertyRank(p) === 1);
  const propertiesWatchlist = properties.filter(
    (p) => getPropertyRank(p) === 2,
  );

  const renderPropertyItem = (property: PropertyData) => (
    <div
      key={property.id}
      className={`property-item clickable ${property.sellDate ? 'sold' : ''}`}
      onClick={() => handlePropertyClick(property.id)}
    >
      {property.sellDate ? (
        <span className="property-status-tag sold">Verkauft</span>
      ) : property.kaufpreis !== undefined &&
        property.kaufpreis !== null &&
        property.kaufdatum ? (
        <span className="property-status-tag owned">In Besitz</span>
      ) : null}
      <div className="property-info">
        <div className="property-address">
          {[
            [property.streetName, property.houseNumber]
              .filter(Boolean)
              .join(' '),
            property.city,
            property.country,
          ]
            .filter(Boolean)
            .join(' • ') ||
            property.address ||
            'No address'}
        </div>
        <div className="property-details">
          <span className="property-value">
            {formatCurrency(property.currentValue || 0)}
          </span>
        </div>
      </div>
      <div className="property-meta">
        {property.kaufpreis !== undefined && property.kaufpreis !== null ? (
          <div
            className={`property-gain-container ${property.gain >= 0 ? 'stat-positive' : 'stat-negative'}`}
          >
            <div className="gain-icon">
              {property.gain >= 0 ? <PolygonUp /> : <PolygonDown />}
            </div>
            <div className="gain-percentage">
              {property.gainPercentage.toFixed(1)}%
            </div>
            <div className="gain-currency">
              {property.gain > 0 ? '+' : ''}
              {formatCurrency(property.gain)}
            </div>
          </div>
        ) : (
          <span
            className="no-purchase-info"
            style={{
              color: 'var(--Grey-2, #a7a3e2)',
              fontSize: '0.9rem',
            }}
          >
            Nicht gekauft
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="properties-page">
      <main className="properties-content">
        <div className="properties-section-header">
          <h2>
            Meine Immobilien
            <span className="property-count">({properties.length})</span>
          </h2>
        </div>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            Lade Immobilien...
          </div>
        ) : properties.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <p>
              Noch keine Immobilien vorhanden. Klicken Sie auf "Neue Immobilie
              hinzufügen", um loszulegen!
            </p>
          </div>
        ) : (
          <>
            {user?.planTier === 'free' && (
              <div
                className="free-tier-banner"
                style={{
                  backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  border: '1px solid #ff9800',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <h4 style={{ margin: '0 0 4px 0', color: '#f57c00' }}>
                    Du nutzt den Free-Tarif
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                    Dein Account ist auf 1 Immobilie und 1 Bewertung beschränkt.
                    Sichere dir jetzt weitere Funktionen!
                  </p>
                </div>
                <button
                  onClick={() => navigate('/shop')}
                  style={{
                    backgroundColor: '#ff9800',
                    color: '#fff',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                >
                  Jetzt Upgraden
                </button>
              </div>
            )}
            <div className="properties-main-content">
              <div className="properties-sections-container">
                {propertiesInBesitz.length > 0 && (
                  <div className="properties-section">
                    <h3 className="properties-section-title">Im Besitz</h3>
                    <div className="properties-list">
                      {propertiesInBesitz.map(renderPropertyItem)}
                    </div>
                  </div>
                )}
                {propertiesVerkauft.length > 0 && (
                  <div className="properties-section">
                    <h3 className="properties-section-title">Verkauft</h3>
                    <div className="properties-list">
                      {propertiesVerkauft.map(renderPropertyItem)}
                    </div>
                  </div>
                )}
                {propertiesWatchlist.length > 0 && (
                  <div className="properties-section">
                    <h3 className="properties-section-title">Watchlist</h3>
                    <div className="properties-list">
                      {propertiesWatchlist.map(renderPropertyItem)}
                    </div>
                  </div>
                )}
              </div>
              <div className="properties-stats">
                <div className="properties-pie-chart">
                  <h4 className="properties-title">Stadtverteilung</h4>
                  <div className="properties-pie-chart-content">
                    {loadingChart ? (
                      <div
                        style={{
                          height: 340,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        Loading chart...
                      </div>
                    ) : (
                      <DonutChart data={chartData} />
                    )}
                  </div>
                </div>
                <div className="properties-map">
                  <h4 className="properties-title">Standorte</h4>
                  <div className="properties-map-content" ref={mapRef}></div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default PropertiesPage;
